import React, { useEffect, useState } from "react";
import { ChevronDown, CircleAlert } from "lucide-react";

import Fold from "@/shared/components/Fold";
import { Counted, ListCard, ListTile } from "@/shared/components/ListCard";
import { cn } from "@/shared/lib/cn";
import type { RunStep } from "@/shared/types";

import RunCode from "./components/RunCode";
import { describeStep, type AppRefs, type StepDescription } from "./stepDescriptions";

export type { AppRefs };

/* A server turn's tool calls in a row, as one `ListCard`: a row per call, a hairline between
   them. Several calls carry the card's header, which sums them up ("Used 4 tools · 1 failed")
   and folds the card; one call is the card alone. A row reads as one sentence with its tile and
   status (a check and its time, Failed, a live timer, Needs you) and opens in place: the
   error, what the call showed, its arguments and its result. A call shows up as soon as the
   model starts writing it, its arguments streaming under its row. The card is the same element
   from the first call on, so a second call never redraws the rows above it. */
export function StepRows({ steps, apps = {} }: { steps: RunStep[]; apps?: AppRefs }) {
  const [folded, setFolded] = useState(false);
  return (
    <ListCard
      label={steps.length > 1 ? <Summary steps={steps} apps={apps} /> : undefined}
      folded={folded}
      onToggle={() => setFolded(!folded)}
      className="min-w-0 fade-in-up"
    >
      {steps.map((step) => <StepRow key={step.id} step={step} apps={apps} />)}
    </ListCard>
  );
}

/** What several calls add up to: their tiles stacked, then how many and how many failed. No
    time: calls the model asks for together run side by side, so their times don't add up. */
function Summary({ steps, apps }: { steps: RunStep[]; apps: AppRefs }) {
  const running = steps.some((step) => step.status === "running");
  const waiting = steps.some((step) => step.status === "waiting");
  const failed = steps.filter((step) => step.status === "failed").length;
  return (
    <span className="flex items-center gap-2.5 min-w-0">
      <span className="flex shrink-0" aria-hidden>
        {steps.slice(0, STACKED).map((step, i) => (
          <span key={step.id} className={cn("w-[22px] h-[22px] rounded-[6px] grid place-items-center overflow-hidden bg-[var(--surface)] text-[var(--text-secondary)] shadow-[0_0_0_1px_var(--hairline-strong),0_0_0_2px_var(--surface-2)] [&_svg]:w-3 [&_svg]:h-3 [&_img]:w-[13px] [&_img]:h-[13px]", i > 0 && "-ml-[5px]")}>
            {describeStep(step, step.status === "running", apps).icon}
          </span>
        ))}
      </span>
      <span className="flex items-baseline gap-1.5 min-w-0 whitespace-nowrap">
        <span className={cn("truncate", running && "shimmer-text")}>
          {running ? "Using tools…" : waiting ? "Waiting for you" : <>Used <Counted n={steps.length} noun="tool" /></>}
        </span>
        {failed > 0 && <span className="shrink-0 font-normal text-[var(--danger)]">· <Counted n={failed} noun="failed" plural="failed" /></span>}
      </span>
    </span>
  );
}

/** The tiles a header stacks, past which it shows none more. */
const STACKED = 5;

/** One call: its tile, its sentence and its status, opening in place. */
function StepRow({ step, apps }: { step: RunStep; apps: AppRefs }) {
  const running = step.status === "running";
  const [open, setOpen] = useState(false);
  // drawn on its first open and kept, so folding it shut animates too
  const [drawn, setDrawn] = useState(false);
  const d = describeStep(step, running, apps);
  const writing = running && !!step.argsText && !WEB_TOOLS.has(step.tool);
  const toggle = () => {
    setDrawn(true);
    setOpen(!open);
  };
  return (
    <div className="min-w-0 fade-in">
      <button
        type="button"
        aria-expanded={open}
        onClick={toggle}
        className={cn("group w-full flex items-center gap-3 min-h-12 px-3 py-2 text-left min-w-0 transition-colors [transition-duration:150ms] outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hairline-focus)]", !open && "hover:bg-[var(--accent-hover)]")}
      >
        <ListTile className="overflow-hidden">{d.icon}</ListTile>
        <span className="flex-1 min-w-0 truncate text-[13.5px] tracking-[-0.005em] text-[var(--text-primary)]">{d.sentence}</span>
        <Status step={step} />
        <Chevron open={open} />
      </button>
      {writing && (
        <div className="px-3 pb-3 pl-14 max-sm:pl-3">
          <RunCode label="Arguments" text={readableArgs(step.argsText!).split("\n").slice(-ARGS_TAIL_LINES).join("\n")} live />
        </div>
      )}
      <Fold open={open}>{drawn && <Details step={step} d={d} writing={writing} />}</Fold>
    </div>
  );
}

/** A call opened: what went wrong, what it showed, then its arguments and its result. While
    the model still writes the arguments, they stream under the row instead. */
function Details({ step, d, writing }: { step: RunStep; d: StepDescription; writing: boolean }) {
  const failed = step.status === "failed";
  return (
    <div className="grid grid-cols-1 gap-3 px-3 pb-3.5 pl-14 max-sm:pl-3 min-w-0">
      {failed && step.result && (
        <div className="flex items-start gap-2 px-2.5 py-2 rounded-[10px] bg-[var(--danger-bg)] ring-1 ring-inset ring-[var(--danger-ring)]">
          <CircleAlert className="w-3.5 h-3.5 mt-[3px] shrink-0 text-[var(--danger)]" strokeWidth={1.75} />
          <p className="m-0 flex-1 min-w-0 text-[13px] leading-[1.45] text-[var(--danger)] [overflow-wrap:anywhere]">{failure(step.result)}</p>
        </div>
      )}
      {!!d.facts?.length && (
        <div className="flex flex-wrap gap-1.5">
          {d.facts.map((fact, i) => (
            <span key={i} className="inline-flex items-center gap-1 h-[22px] max-w-full px-2 rounded-[6px] bg-[var(--surface-2)] text-[12px] text-[var(--text-secondary)] truncate">{fact}</span>
          ))}
        </div>
      )}
      {d.extra}
      {!writing && d.code && <RunCode label={d.code.label} text={d.code.text} />}
      {!writing && !d.code && !d.hideArgs && <RunCode label="Arguments" text={JSON.stringify(d.args ?? step.args)} />}
      {!d.hideArgs && (step.result == null
        ? step.status === "running" && <p className="m-0 text-[12.5px] text-[var(--text-tertiary)]">Waiting for the result…</p>
        : !failed
          ? <RunCode label="Result" text={step.result} />
          : saysMore(step.result) && <RunCode label="Error" text={withoutPrefix(step.result)} />)}
    </div>
  );
}

/** The row's status: a check and its time, Failed, a live timer, Needs you, Didn't finish. */
function Status({ step }: { step: RunStep }) {
  const base = "shrink-0 inline-flex items-center gap-1.5 text-[12px] tabular-nums whitespace-nowrap";
  switch (step.status) {
    case "running":
      return <span className={cn(base, "text-[var(--text-tertiary)]")}><Spinner /><Elapsed /></span>;
    case "waiting":
      return (
        <span className="shrink-0 inline-flex items-center gap-1.5 h-5 px-2 rounded-[6px] bg-[var(--warn-bg)] ring-1 ring-inset ring-[var(--warn-ring)] text-[11.5px] font-medium text-[var(--warn-text)]">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--warn)]" />Needs you
        </span>
      );
    case "failed":
      return <span className={cn(base, "text-[var(--danger)]")}><CircleAlert className="w-3.5 h-3.5" strokeWidth={1.75} />Failed</span>;
    case "stopped":
      return <span className={cn(base, "text-[var(--text-quaternary)]")}>Didn’t finish</span>;
    default:
      return (
        <span className={cn(base, "text-[var(--text-tertiary)]")}>
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-none stroke-[var(--text-primary)] [stroke-width:1.8] [stroke-linecap:round] [stroke-linejoin:round]" aria-hidden>
            <path className="check-draw" pathLength={1} d="M5 12.5 10 17.5 19 7" />
          </svg>
          {step.ms > 0 && duration(step.ms)}
        </span>
      );
  }
}

function Spinner() {
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 animate-spin motion-reduce:animate-none [animation-duration:800ms] fill-none [stroke-width:1.6] [stroke-linecap:round]" aria-hidden>
      <circle cx="12" cy="12" r="8.5" className="stroke-current opacity-25" />
      <path d="M12 3.5a8.5 8.5 0 0 1 8.5 8.5" className="stroke-[var(--text-primary)]" />
    </svg>
  );
}

const Chevron = ({ open }: { open: boolean }) => (
  <ChevronDown
    className={cn("w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)] group-hover:text-[var(--text-secondary)] transition-transform [transition-duration:360ms] [transition-timing-function:var(--easing)]", open && "rotate-180")}
    strokeWidth={1.75}
  />
);

/** A call's time: "0.4s", "1.5s", "12s"; a quicker one reads "0.1s". */
const duration = (ms: number) => (ms >= 9950 ? `${Math.round(ms / 1000)}s` : `${(Math.max(ms, 100) / 1000).toFixed(1)}s`);

/** A failed call's reply without the "error: " the model is told. */
const withoutPrefix = (result: string) => result.replace(/^error:\s*/i, "").trim();

/** A failed call's reply in one line: an app's JSON `error`, else its first line. */
function failure(result: string): string {
  const said = withoutPrefix(result);
  try {
    const parsed: unknown = JSON.parse(said);
    const error = (parsed as { error?: unknown; message?: unknown } | null)?.error ?? (parsed as { message?: unknown } | null)?.message;
    if (typeof error === "string" && error.trim()) return error.trim();
  } catch {
    // not JSON: its first line
  }
  return said.split("\n")[0];
}

/** The reply says more than its line (a JSON body, a command's output): the whole of it shows
    under the error, as written. */
const saysMore = (result: string) => withoutPrefix(result) !== failure(result);

// their rows say what they look for, so the arguments being written don't stream under them
const WEB_TOOLS = new Set(["web_search", "web_fetch"]);

/** What a running call is doing, in a few words: the turn's header while it runs. */
export function stepPhase(step: RunStep, apps: AppRefs): string {
  if (step.status === "waiting") return "Waiting for you";
  if (step.tool === "ask_user") return "Asking you";
  return describeStep(step, true, apps).phase;
}

/* Seconds since `since` (epoch ms), as "4s" or "1m 05s" — or since mount without one: the
   header only says "Thinking" while the turn is in flight. A server clock a little ahead
   never shows below zero. */
export function Elapsed({ since }: { since?: number }) {
  const [start] = useState(() => since ?? Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);
  return <span className="tabular-nums">{clock(Math.max(0, Math.floor((now - start) / 1000)))}</span>;
}

/** Seconds as "4s" or "1m 05s". */
export const clock = (s: number) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`);

/** The arguments a model is writing, as they read: JSON's escapes undone. Ceiling: it
    unescapes by pattern on a fragment, without parsing it. */
const readableArgs = (args: string) => args.replace(/\\n/g, "\n").replace(/\\t/g, "  ").replace(/\\"/g, '"');

/** Shown while a call is written: its last lines, never the whole of a long file. */
const ARGS_TAIL_LINES = 6;
